import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { User, Calendar, Eye, Heart, MessageCircle, ArrowLeft, Twitter, Instagram, Linkedin, Globe } from 'lucide-react';
import TreeLogo from '@/components/TreeLogo';
import TreeLoadingSpinner from '@/components/TreeLoadingSpinner';

const AuthorPage = () => {
  const { authorId } = useParams();
  const navigate = useNavigate();
  const [author, setAuthor] = useState(null);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAuthor();
  }, [authorId]);

  const fetchAuthor = async () => {
    try {
      const response = await axios.get(`${API}/api/authors/${authorId}`);
      setAuthor(response.data.author);
      setPosts(response.data.posts);
    } catch (error) {
      console.error('Error fetching author:', error);
      navigate('/blog');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getSocialIcon = (platform) => {
    switch (platform) {
      case 'twitter': return <Twitter className="w-5 h-5" />;
      case 'instagram': return <Instagram className="w-5 h-5" />;
      case 'linkedin': return <Linkedin className="w-5 h-5" />;
      default: return <Globe className="w-5 h-5" />;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <TreeLoadingSpinner size="lg" />
      </div>
    );
  }

  if (!author) return null;

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'hsl(45 25% 97%)' }}>
      {/* Navigation */}
      <nav className="sticky top-0 z-50" style={{ backgroundColor: 'hsl(220 15% 55%)' }}>
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: 'hsl(15 40% 82%)' }}>
              <TreeLogo size={24} className="text-slate-600" />
            </div>
            <span className="text-lg text-white font-light" style={{ fontFamily: 'Playfair Display, serif' }}>
              Rooted Blog
            </span>
          </Link>
          
          <Button 
            variant="ghost" 
            className="text-white/80 hover:text-white"
            onClick={() => navigate('/blog')}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            All Posts
          </Button>
        </div>
      </nav>

      <div className="max-w-4xl mx-auto px-6 py-12">
        {/* Author Profile Header */}
        <Card className="p-8 mb-12 text-center">
          {author.photo_url ? (
            <img 
              src={author.photo_url} 
              alt={author.name} 
              className="w-32 h-32 rounded-full mx-auto mb-6 object-cover border-4"
              style={{ borderColor: 'hsl(15 40% 82%)' }}
            />
          ) : (
            <div 
              className="w-32 h-32 rounded-full mx-auto mb-6 flex items-center justify-center"
              style={{ backgroundColor: 'hsl(15 40% 85%)' }}
            >
              <User className="w-16 h-16" style={{ color: 'hsl(220 15% 40%)' }} />
            </div>
          )}
          
          <h1 className="text-3xl mb-2" style={{ fontFamily: 'Playfair Display, serif', color: 'hsl(220 15% 25%)' }}>
            {author.name}
          </h1>
          
          {author.affiliation && (
            <p className="text-lg mb-4" style={{ color: 'hsl(220 10% 50%)' }}>
              {author.affiliation}
            </p>
          )}
          
          {author.bio && (
            <p className="max-w-2xl mx-auto mb-6" style={{ color: 'hsl(220 10% 40%)' }}>
              {author.bio}
            </p>
          )}
          
          {/* Social Links */}
          {author.social_links && Object.keys(author.social_links).length > 0 && (
            <div className="flex justify-center gap-3">
              {Object.entries(author.social_links).map(([platform, url]) => (
                url && (
                  <Button 
                    key={platform}
                    variant="outline" 
                    size="sm" 
                    className="rounded-full p-2"
                    onClick={() => window.open(url, '_blank')}
                  >
                    {getSocialIcon(platform)}
                  </Button>
                )
              ))}
            </div>
          )}
          
          {/* Stats */}
          <div className="flex justify-center gap-8 mt-6 pt-6 border-t" style={{ borderColor: 'hsl(220 10% 90%)' }}>
            <div className="text-center">
              <p className="text-2xl font-semibold" style={{ color: 'hsl(220 15% 30%)' }}>{posts.length}</p>
              <p className="text-sm" style={{ color: 'hsl(220 10% 55%)' }}>Articles</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-semibold" style={{ color: 'hsl(220 15% 30%)' }}>
                {posts.reduce((sum, p) => sum + (p.views_count || 0), 0)}
              </p>
              <p className="text-sm" style={{ color: 'hsl(220 10% 55%)' }}>Total Views</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-semibold" style={{ color: 'hsl(220 15% 30%)' }}>
                {posts.reduce((sum, p) => sum + (p.likes_count || 0), 0)}
              </p>
              <p className="text-sm" style={{ color: 'hsl(220 10% 55%)' }}>Total Likes</p>
            </div>
          </div>
        </Card>

        {/* Author's Posts */}
        <h2 className="text-2xl mb-6" style={{ fontFamily: 'Playfair Display, serif', color: 'hsl(220 15% 30%)' }}>
          Articles by {author.name}
        </h2>
        
        {posts.length === 0 ? (
          <Card className="p-8 text-center">
            <p style={{ color: 'hsl(220 10% 50%)' }}>No published articles yet.</p>
          </Card>
        ) : (
          <div className="space-y-6">
            {posts.map(post => (
              <Card 
                key={post.post_id}
                className="overflow-hidden hover:shadow-lg transition-shadow cursor-pointer"
                onClick={() => navigate(`/blog/${post.slug}`)}
              >
                <div className="flex flex-col md:flex-row">
                  {post.featured_image && (
                    <div className="md:w-64 flex-shrink-0">
                      <img 
                        src={post.featured_image} 
                        alt={post.title}
                        className="w-full h-48 md:h-full object-cover"
                      />
                    </div>
                  )}
                  <div className="p-6 flex-1">
                    {post.tags?.length > 0 && (
                      <div className="flex flex-wrap gap-1 mb-2">
                        {post.tags.slice(0, 3).map(tag => (
                          <Badge key={tag} variant="secondary" className="text-xs">
                            {tag}
                          </Badge>
                        ))}
                      </div>
                    )}
                    
                    <h3 className="text-xl font-semibold mb-2" style={{ fontFamily: 'Playfair Display, serif', color: 'hsl(220 15% 25%)' }}>
                      {post.title}
                    </h3>
                    
                    {post.subtitle && (
                      <p className="text-sm mb-2" style={{ color: 'hsl(220 10% 50%)' }}>
                        {post.subtitle}
                      </p>
                    )}
                    
                    <p className="text-sm mb-4 line-clamp-2" style={{ color: 'hsl(220 10% 45%)' }}>
                      {post.excerpt}
                    </p>
                    
                    <div className="flex items-center justify-between text-xs" style={{ color: 'hsl(220 10% 55%)' }}>
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {formatDate(post.published_at)}
                      </span>
                      <div className="flex items-center gap-3">
                        <span className="flex items-center gap-1">
                          <Eye className="w-3 h-3" />
                          {post.views_count}
                        </span>
                        <span className="flex items-center gap-1">
                          <Heart className="w-3 h-3" />
                          {post.likes_count}
                        </span>
                        <span className="flex items-center gap-1">
                          <MessageCircle className="w-3 h-3" />
                          {post.comments_count}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Footer */}
      <footer className="py-8 px-6 text-center" style={{ backgroundColor: 'hsl(220 15% 55%)' }}>
        <p className="text-white/70 text-sm">
          © {new Date().getFullYear()} Rooted Faith Journal. All rights reserved.
        </p>
      </footer>
    </div>
  );
};

export default AuthorPage;
